import { clsx } from 'clsx';
import {
  ClipboardEvent,
  ComponentType,
  FC,
  KeyboardEvent,
  PropsWithChildren,
  ReactNode,
  useRef,
  useState,
} from 'react';
import { IconButton } from '../button/icon-button.tsx';
import { XmarkIcon } from '../icons/react';
import { useControllableState } from '../utils/use-controllable-state.tsx';
import {
  multiTag,
  multiTagBottomTip,
  multiTagError,
  multiTagIsFocused,
  multiTagLabel,
  multiTagWithError,
  tagItem,
} from './multi-tag.css.ts';
import { normalizeTagForRegex } from './normalize-tag-for-regex.ts';

export const MultiTag = ({
  errorMessage,
  label,
  className,
  placeholder,
  value,
  onChange,
  defaultValue = [],
  maxItems,
  bottomTip,
  TagItem,
  regex,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  defaultValue?: string[];
  label?: string;
  errorMessage?: string;
  placeholder?: string;
  className?: string;
  maxItems?: number;
  bottomTip?: ReactNode;
  TagItem: ComponentType<{
    children: ReactNode;
    onRemove: () => void;
  }>;
  regex?: string;
}) => {
  const [tags, setTags] = useControllableState<string[]>({
    value,
    onChange,
    defaultValue,
  });
  const [isFocused, setIsFocused] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const addTags = (tagValues: string[], clearInput?: () => void) => {
    if (maxItems && tags.length >= maxItems) {
      clearInput?.();
      return;
    }

    const uniqueTags = tagValues.filter(
      (tag, index, self) => self.indexOf(tag) === index,
    );

    const normalizedTags: string[] = [];

    for (const tag of uniqueTags) {
      const normalized = tag.trim();
      if (!normalized) {
        clearInput?.();
        continue;
      }

      if (tags.some((t) => t.toLowerCase() === normalized.toLowerCase())) {
        setValidationError(`Item already exists`);
        continue;
      }

      if (regex) {
        try {
          const regexObj = new RegExp(regex);
          if (!regexObj.test(normalized)) {
            setValidationError(`Item doesn't match the required format`);
            continue;
          }
        } catch {
          continue;
        }
      }

      normalizedTags.push(normalized);

      if (maxItems && tags.length + normalizedTags.length >= maxItems) break;
    }

    if (normalizedTags.length > 0) {
      clearInput?.();
      setValidationError(null);
      setTags((prev) => [...prev, ...normalizedTags]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const keys = ['Enter', ',', ' '];
    if (keys.includes(e.key)) {
      addTags([e.currentTarget.value], () => (e.currentTarget.value = ''));
      e.preventDefault();
    } else if (e.key === 'Backspace' && e.currentTarget.value === '') {
      // remove tag on backspace
      setTags((prev) => prev.slice(0, -1));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').split(/[\s,]+/);

    let regexObj: RegExp | undefined;
    if (regex) {
      try {
        regexObj = new RegExp(regex);
      } catch {
        regexObj = undefined;
      }
    }

    addTags(pasted.map((tag) => normalizeTagForRegex(tag, regexObj)));
    e.preventDefault();
  };

  const removeTag = (index: number) => {
    setTags((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div>
      {label && <label className={multiTagLabel}>{label}</label>}
      <div
        className={clsx(
          multiTag,
          isFocused && multiTagIsFocused,
          errorMessage && multiTagWithError,
          className,
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag, index) => (
          <TagItem key={index} onRemove={() => removeTag(index)}>
            {tag}
          </TagItem>
        ))}
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={() => setValidationError(null)}
        />
      </div>
      {errorMessage || validationError ? (
        <p className={multiTagError}>{errorMessage || validationError}</p>
      ) : null}
      {bottomTip ? <div className={multiTagBottomTip}>{bottomTip}</div> : null}
    </div>
  );
};

export const MultiTagItem: FC<
  PropsWithChildren<{
    onRemove: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    className?: string;
  }>
> = ({ onRemove, children, className }) => (
  <div className={clsx(tagItem, className)}>
    {children}
    <IconButton size={'xSmall'} variant={'ghost'} onClick={onRemove}>
      <XmarkIcon />
    </IconButton>
  </div>
);
